// The production chain: BuildQueue (fixed 10-slot char queue), BuilderGob (the credit-drip
// build loop in DefUpdate + queue management), MobileUnitBuilderGob (the build-complete
// delivery state), FindNearestFreeTile (the 224-entry outward spiral — SIM DATA: delivery
// positions come from it), and BuildMgr (the AI build pipeline UnitGroups feed).
// game/Builder.cpp + game/MobileBuilder.cpp + game/BuildMgr.cpp + ht.h:6990-7050.

import type { SimContext } from '../context.ts';
import { kfGobBeingBuilt, kfGobBeingUpgraded, kfGobStructure } from './gob.ts';
import { UnitGob } from './unitgob.ts';
import { StructGob } from './struct.ts';
import {
  BuilderConsts,
  StructConsts,
  MobileUnitBuilderConsts,
  kfUntcNotifyPowerLowHigh,
} from './unitconsts.ts';
import { kfUnitRepairing } from '../player/masks.ts';
import { kumBuilder, kumStructures } from '../player/masks.ts';
import { kfHcapDecreasedTimeToBuild, kfPlrComputer } from '../player/side.ts';
import type { Player } from '../player/player.ts';
import {
  Message,
  newMessage,
  kgidNull,
  ksmidNull,
  knHandled,
  kstReservedGlobal,
  kmidReservedEnter,
  kmidReservedExit,
  kmidReservedUpdate,
  kmidBuildOtherCommand,
  kmidAbortBuildOtherCommand,
  kmidSelfDestructCommand,
  kmidUpgradeComplete,
  kmidBeingUpgraded,
  kmidMineCommand,
  kstIdle,
  kstBuildOtherCompleting,
} from '../sm/message.ts';
import { TcFromWc, WcFromTc } from '../math/coords.ts';
import { itofx } from '../math/fix.ts';
import { idiv } from '../math/int32.ts';
import { kbfStructure, kbfMobileUnit } from '../terrain/terrainmap.ts';
import { FindNearestFreeTile } from './tilesearch.ts';
import { kwxInvalid } from './mobileunit.ts';
import { CreateGob } from './factory.ts';
import { kgtGalaxMiner } from './gobtypes.ts';
import { kutGalaxMiner } from '../player/side.ts';
import {
  knLimitStruct,
  knLimitMobileUnit,
  GetGlobalQueuedCount,
  GetQueuedCount,
} from './gobmgr.ts';
import type { WPoint } from '../types.ts';

// game/ht.h:300
export const knDecreasedTimeToBuildPercent = -20;

// mpshared/side.h:20
export const kutNone = -1;

// game/ht.h:6990
export const kcBuildQueueMax = 10;

// game/Builder.cpp:1074-1204 + ht.h:6992-7016 — BUG-COMPAT MEMORY MODEL (tasks/sim-quirks.md A1/A2/A3/B3).
//
// The C++ BuildQueue is { char m_achBuildQueue[10]; word m_cchQueueMax; } with NO bounds
// checks in release. Enqueue scans for the first free slot with `while (slot[i] != kutNone
// && i < m_cchQueueMax) i++` and then writes slot[i] UNCONDITIONALLY — so enqueueing into
// a FULL queue writes slot[m_cchQueueMax]. For the default max=10 that byte IS the low
// byte (LE) of m_cchQueueMax itself: ordering an 11th unit sets the queue's capacity to
// the unit type (SRInfantry=0 -> capacity 0). From then on every Enqueue writes slot[0],
// REPLACING the currently-building head (the order "jumps the queue", inheriting the
// credits already spent), and GetUnitCount scans 0 slots (queued count drops out of the
// IsBelowLimit math). This is live, recorded, hash-agreed shipping behavior (easy-2p-a:
// side 2's LRInfantry ordered at update 4675 builds ~125 updates later despite 8 queued
// SRInfantry) — so the port models the struct as 12 addressable bytes: slots 0-9 plus the
// capacity word at bytes 10-11. Reads/writes beyond byte 11 (possible only if a
// corrupted capacity exceeds 12) throw loudly rather than silently invent memory.
export class BuildQueue {
  // bytes 0..9 = m_achBuildQueue, bytes 10..11 = m_cchQueueMax (little-endian word)
  private mem = new Int8Array(12);
  // the byte before the array = the high byte of the BuildQueue's own VPTR (the class
  // has virtual Load/SaveState, so the vtable pointer sits at offset 0 and
  // m_achBuildQueue at offset 4 on the 32-bit LE build). A spill write copies the old
  // head byte there: a real unit type (< 23) rewrites the 0 a 24-bit emscripten
  // vtable address already had (inert); kutNone (0xff, empty-queue cancel) corrupts
  // the vptr — a latent C++ crash. Modeled as a side slot so state isn't lost.
  private spillMinus1 = -1;

  constructor() {
    this.mem.fill(kutNone, 0, kcBuildQueueMax);
    this.setMax(kcBuildQueueMax); // Builder.cpp:1091-1096
  }

  private getMax(): number {
    return (this.mem[10] & 0xff) | ((this.mem[11] & 0xff) << 8);
  }
  private setMax(v: number): void {
    this.mem[10] = v & 0xff;
    this.mem[11] = (v >> 8) & 0xff;
  }

  private readByte(i: number): number {
    if (i === -1) return this.spillMinus1;
    if (i >= 0 && i < 12) return this.mem[i];
    throw new Error(
      `BuildQueue read at byte ${i}: beyond the C++ struct (undefined behavior territory)`,
    );
  }

  private writeByte(i: number, v: number): void {
    if (i === -1) {
      this.spillMinus1 = (v << 24) >> 24;
      return;
    }
    if (i >= 0 && i < 12) {
      this.mem[i] = v;
      return;
    }
    throw new Error(
      `BuildQueue write at byte ${i}: beyond the C++ struct (undefined behavior territory)`,
    );
  }

  // Builder.cpp:1074-1090 — m_cchQueueMax round-trips VERBATIM (including a corrupted
  // value from the enqueue-on-full overflow); the RLE covers exactly max bytes
  LoadState(pstm: import('../sm/stream.ts').Stream, complete = false): boolean {
    // issue #96: a COMPLETE snapshot stores the raw 12-byte struct image + the spill byte,
    // so an A1-overflow-corrupted queue (max=0 but slot 0 occupied) round-trips EXACTLY.
    // The faithful path's max-gated RLE drops slot 0 of such a queue → unloadable/divergent.
    if (complete) {
      for (let i = 0; i < 12; i++) this.mem[i] = (pstm.ReadByte() << 24) >> 24;
      this.spillMinus1 = (pstm.ReadByte() << 24) >> 24;
      return true;
    }
    this.setMax(pstm.ReadWord());
    const max = this.getMax();
    if (max > 0) {
      if (max > 12)
        throw new Error(`BuildQueue.LoadState: corrupted max ${max} exceeds the modeled struct`);
      const tmp = new Uint8Array(max);
      pstm.ReadBytesRLE(tmp);
      for (let i = 0; i < max; i++) this.writeByte(i, (tmp[i] << 24) >> 24);
    }
    return true;
  }

  SaveState(pstm: import('../sm/stream.ts').Stream, complete = false): boolean {
    if (complete) {
      for (let i = 0; i < 12; i++) pstm.WriteByte(this.mem[i] & 0xff);
      pstm.WriteByte(this.spillMinus1 & 0xff);
      return true;
    }
    const max = this.getMax();
    pstm.WriteWord(max);
    if (max > 0) {
      if (max > 12)
        throw new Error(`BuildQueue.SaveState: corrupted max ${max} exceeds the modeled struct`);
      const tmp = new Uint8Array(max);
      for (let i = 0; i < max; i++) tmp[i] = this.readByte(i) & 0xff;
      pstm.WriteBytesRLE(tmp);
    }
    return true;
  }

  SetSize(cutMax: number): void {
    this.setMax(cutMax);
  }

  GetRemainingCapacity(): number {
    let cSlotsOpen = 0;
    for (let i = 0; i < this.getMax(); i++) {
      if (this.readByte(i) === kutNone) cSlotsOpen++;
    }
    return cSlotsOpen;
  }

  // Builder.cpp:1114-1126 — including the unchecked write at slot[max] on a full queue
  Enqueue(ut: number): void {
    let i = 0;
    while (this.readByte(i) !== kutNone && i < this.getMax()) i++;
    this.writeByte(i, ut);
  }

  // Builder.cpp:1128-1134 — memmove over the kcBuildQueueMax CONSTANT (not m_cchQueueMax)
  Dequeue(): void {
    for (let i = 0; i < kcBuildQueueMax - 1; i++) this.mem[i] = this.mem[i + 1];
    this.mem[kcBuildQueueMax - 1] = kutNone;
  }

  // Builder.cpp:1136-1161 — back-to-front search; NOT-FOUND falls through with i = -1
  // (the C++ comment blesses it: "we will encounter things being removed that aren't
  // there because of a lag in state") and the memmove then shifts the whole window left
  // one byte starting at slot[-1]. Returns true if the CURRENTLY BUILDING slot 0 went.
  RemoveUnit(ut: number): boolean {
    const max = this.getMax();
    let i: number;
    for (i = max - 1; i > -1; i--) {
      if (this.readByte(i) === ut) break;
    }
    // memmove(&slot[i], &slot[i+1], max - i): forward copy, dst < src
    for (let j = 0; j < max - i; j++) this.writeByte(i + j, this.readByte(i + j + 1));
    this.writeByte(max - 1, kutNone);
    return i === 0;
  }

  Peek(): number {
    return this.mem[0];
  }
  IsEmpty(): boolean {
    return this.mem[0] === kutNone;
  }
  IsFull(): boolean {
    return this.readByte(this.getMax() - 1) !== kutNone;
  }

  GetUnitCount(ut: number): number {
    let cut = 0;
    for (let i = 0; i < this.getMax(); i++) {
      if (ut === kutNone) {
        if (this.readByte(i) !== kutNone) cut++;
      } else {
        if (this.readByte(i) === ut) cut++;
      }
    }
    return cut;
  }

  // Builder.cpp:1196-1199 — memset over the kcBuildQueueMax CONSTANT (does NOT repair a
  // corrupted m_cchQueueMax)
  Clear(): void {
    this.mem.fill(kutNone, 0, kcBuildQueueMax);
  }

  // Builder.cpp:1201-1207 operator= — copies m_cchQueueMax BYTES (the possibly-corrupted
  // new max), not the full array
  CopyFrom(bqRHS: BuildQueue): void {
    this.setMax(bqRHS.getMax());
    for (let i = 0; i < this.getMax(); i++) this.mem[i] = bqRHS.readByte(i);
  }
}

export class BuilderGob extends StructGob {
  m_bq = new BuildQueue();
  m_gidBuildVisible = kgidNull;
  m_fxHealthBuilding = itofx(0);
  m_nCreditsSpentOnBuilding = 0;
  m_nCostRemainder = 0;
  m_iLastSelection = 0;
  m_tptRally = { tx: -100, ty: -100 }; // ktxInvalid

  get m_pbldrc(): BuilderConsts {
    return this.m_puntc as BuilderConsts;
  }

  // game/Builder.cpp:32-40
  constructor(ctx: SimContext, pbldrc: BuilderConsts) {
    super(ctx, pbldrc);
  }

  // game/Builder.cpp:46-61
  override Init(wx: number, wy: number, pplr: Player, fxHealth: number, ff: number): boolean {
    if (!super.Init(wx, wy, pplr, fxHealth, ff)) return false;

    // Rally point: center spot "at the bottom" middle
    const wrc = this.GetTilePaddedWRect();
    this.m_tptRally.tx = TcFromWc(wrc.left + idiv(wrc.right - wrc.left, 3));
    this.m_tptRally.ty = TcFromWc(wrc.bottom);
    return true;
  }

  // game/Builder.cpp:96-106 — half-baked buildings blow when the builder goes bye-bye
  override Deactivate(): void {
    this.AbortBuild();
    super.Deactivate();
  }

  IsBuildInProgress(): boolean {
    return !this.m_bq.IsEmpty();
  } // ht.h:7043
  UnitBuildInProgress(): number {
    return this.m_bq.Peek();
  } // ht.h:7046
  GetLastSelection(): number {
    return this.m_iLastSelection;
  }

  // game/Builder.cpp:108-143 — the queued counts live in gobmgr.ts (free functions; an
  // ESM class-extends cycle forbids gobmgr -> builder). These statics keep the C++ shape.
  static GetGlobalQueuedCount = GetGlobalQueuedCount;
  static GetQueuedCount = GetQueuedCount;

  // game/Builder.cpp:65-90 (knVerBuilderGobState 4) — the build-progress block exists
  // only when the queue is non-empty
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 4) return false;
    const complete = this.ctx.snapshotComplete;
    this.m_bq.LoadState(pstm, complete);
    if (!this.m_bq.IsEmpty()) {
      this.m_gidBuildVisible = pstm.ReadWord();
      this.m_fxHealthBuilding = (pstm.ReadWord() << 16) >> 16;
      this.m_nCreditsSpentOnBuilding = pstm.ReadWord(); // int in C++ — ZERO-extends
      this.m_nCostRemainder = pstm.ReadWord(); // int in C++ — ZERO-extends
      if (!complete) {
        // FAITHFUL: rally rides inside the build-progress block
        this.m_tptRally.tx = (pstm.ReadWord() << 16) >> 16;
        this.m_tptRally.ty = (pstm.ReadWord() << 16) >> 16;
      }
    }
    // issue #96 B24 (snapshotComplete): the rally is serialized UNCONDITIONALLY — the C++
    // saved it only mid-build, so an idle builder's rally was lost on load and its next unit
    // rallied to the invalid (-100,-100) default, diverging from a from-scratch sim.
    if (complete) {
      this.m_tptRally.tx = (pstm.ReadWord() << 16) >> 16;
      this.m_tptRally.ty = (pstm.ReadWord() << 16) >> 16;
    }
    return super.LoadState(pstm);
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(4);
    const complete = this.ctx.snapshotComplete;
    this.m_bq.SaveState(pstm, complete);
    if (!this.m_bq.IsEmpty()) {
      pstm.WriteWord(this.m_gidBuildVisible);
      pstm.WriteWord(this.m_fxHealthBuilding);
      pstm.WriteWord(this.m_nCreditsSpentOnBuilding);
      pstm.WriteWord(this.m_nCostRemainder);
      if (!complete) {
        // FAITHFUL: rally rides inside the build-progress block
        pstm.WriteWord(this.m_tptRally.tx);
        pstm.WriteWord(this.m_tptRally.ty);
      }
    }
    if (complete) {
      // B24: rally always present
      pstm.WriteWord(this.m_tptRally.tx);
      pstm.WriteWord(this.m_tptRally.ty);
    }
    return super.SaveState(pstm);
  }

  // game/Builder.cpp:145-155

  Build(ut: number, gid: number = kgidNull): void {
    this.m_bq.Enqueue(ut);
    this.m_gidBuildVisible = gid;
    this.m_unvl.MinSkip(); // the queue is acted on during Update
  }

  // game/Builder.cpp:157-205
  AbortBuild(fRefundCreditsSpentOnBuilding: boolean = false, ut: number = kutNone): void {
    if (this.m_bq.IsEmpty()) return;

    let fCurrentConstruction = true;

    if (ut === kutNone) {
      // Flush the queue; a structure under construction self-destructs valueless
      this.m_bq.Clear();

      const pgobBuild = this.ctx.gobm.GetGob(this.m_gidBuildVisible, false) as StructGob | null;
      if (pgobBuild !== null) {
        pgobBuild.SetFlags((pgobBuild.GetFlags() & ~kfGobBeingBuilt) >>> 0);
        pgobBuild.SelfDestruct(false);
      }
    } else {
      fCurrentConstruction = this.m_bq.RemoveUnit(ut);
    }

    if (fCurrentConstruction) {
      if (fRefundCreditsSpentOnBuilding) {
        (this.m_pplr as Player).SetCredits(
          (this.m_pplr as Player).GetCredits() + this.m_nCreditsSpentOnBuilding,
          false,
        );
        (this.m_pplr as Player).ModifyTotalCreditsConsumed(-this.m_nCreditsSpentOnBuilding);
      }

      this.m_nCreditsSpentOnBuilding = 0;
      this.m_nCostRemainder = 0;
      this.m_fxHealthBuilding = itofx(0);
      this.WaitingForCredits(false);
    }
  }

  // game/Builder.cpp:207-312 — the per-update build drip
  override DefUpdate(): void {
    super.DefUpdate();

    if (this.m_bq.IsEmpty()) return;

    if (this.m_ff & kfGobBeingUpgraded) {
      // Building pauses; keep the credits display honest
      const nCredits = (this.m_pplr as Player).GetCredits();
      this.WaitingForCredits(nCredits === 0);
      return;
    }

    const puntc = this.ctx.unitConsts[this.m_bq.Peek()] as StructConsts;
    const pgobBuild = this.ctx.gobm.GetGob(this.m_gidBuildVisible, false) as UnitGob | null;

    // (god mode pinned off in equivalence runs; the SP-only instant build is inert)
    let fBuild = false;
    {
      // CostPerUpdate is a fraction; the remainder carries into the next update
      const nCost = puntc.GetCost() + this.m_nCostRemainder;
      let cupdTimeToBuild = puntc.GetTimeToBuild();
      if (this.m_pplr.GetHandicap() & kfHcapDecreasedTimeToBuild) {
        cupdTimeToBuild = idiv(cupdTimeToBuild * (100 + knDecreasedTimeToBuildPercent) + 50, 100);
      }

      // Low power inflates TimeToBuild
      if (this.m_puntc.wf & kfUntcNotifyPowerLowHigh && (this.m_pplr as Player).IsPowerLow()) {
        cupdTimeToBuild *= 2;
      }

      const nCostPerUpdate = idiv(nCost, cupdTimeToBuild);

      const nCredits = (this.m_pplr as Player).GetCredits() - nCostPerUpdate;
      if (nCredits >= 0) {
        this.WaitingForCredits(false);

        (this.m_pplr as Player).SetCredits(nCredits, true);
        this.m_nCreditsSpentOnBuilding += nCostPerUpdate;
        this.m_nCostRemainder = nCost % cupdTimeToBuild;

        const fxHealthPerUpdate = idiv(puntc.GetArmorStrength(), cupdTimeToBuild);
        let fxHealth = (this.m_fxHealthBuilding + fxHealthPerUpdate) | 0;

        if (this.m_nCreditsSpentOnBuilding >= puntc.GetCost()) {
          fxHealth = puntc.GetArmorStrength();
          fBuild = true;
        }
        this.m_fxHealthBuilding = fxHealth;
        if (pgobBuild !== null) pgobBuild.SetHealth(this.m_fxHealthBuilding);

        this.MarkRedraw();
      } else {
        if (!(this.m_wfUnit & kfUnitRepairing)) {
          this.WaitingForCredits(true);
        }
        if (pgobBuild !== null) pgobBuild.MarkRedraw();
      }

      this.m_unvl.MinSkip();
    }

    if (fBuild) {
      if (pgobBuild !== null) {
        pgobBuild.SetHealth(puntc.GetArmorStrength());
        pgobBuild.SetFlags((pgobBuild.GetFlags() & ~kfGobBeingBuilt) >>> 0);
      }

      if (this.m_pplr === this.ctx.plrLocal) this.ctx.host.PlaySfx(this.m_pbldrc.sfxUnitReady);
      this.SetState(kstBuildOtherCompleting);
    }
  }

  // game/Builder.cpp:391-401 — buildings are in the gobm while building; vehicles
  // don't exist until done
  GetBuiltGob(): UnitGob | null {
    const pgobBuild = this.ctx.gobm.GetGob(this.m_gidBuildVisible) as UnitGob | null;
    if (pgobBuild !== null) return pgobBuild;
    return CreateGob(this.ctx, this.ctx.unitConsts[this.m_bq.Peek()].gt) as UnitGob | null;
  }

  // game/Builder.cpp:403-415
  ClearBuiltGob(): void {
    this.m_bq.Dequeue();
    this.m_gidBuildVisible = kgidNull;
    this.m_nCostRemainder = 0;
    this.m_nCreditsSpentOnBuilding = 0;
    this.m_fxHealthBuilding = itofx(0);
  }

  // game/Builder.cpp:428-433
  SyncBuildQueue(pbq: BuildQueue): void {
    pbq.CopyFrom(this.m_bq);
  }

  // game/Builder.cpp:475-486
  FindInitPosition(): WPoint {
    const wrc = this.GetTilePaddedWRect();
    const tx = TcFromWc(wrc.left + idiv(wrc.right - wrc.left, 3));
    const ty = TcFromWc(wrc.bottom);
    return FindNearestFreeTile(this.ctx, tx, ty);
  }
}

// game/MobileBuilder.cpp — the mobile-unit-producing builder (HRC/VTS base)
export class MobileUnitBuilderGob extends BuilderGob {
  // game/MobileBuilder.cpp:17-20 — the ctor mutates the SHARED consts (idempotent |=):
  // every mobile-unit builder wants power notification, and the same flag gates the
  // power-low build slowdown in BuilderGob's drip
  constructor(ctx: SimContext, pmubc: MobileUnitBuilderConsts) {
    super(ctx, pmubc);
    this.m_puntc.wf |= kfUntcNotifyPowerLowHigh;
  }

  // game/MobileBuilder.cpp:77-202
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.SetState(kstIdle);
          return knHandled;

        case kmidBuildOtherCommand:
          // The bedrock limit decision: queued-order UIs only guess; THIS runs
          // identically on every client
          if (this.ctx.gobm.IsBelowLimit(knLimitMobileUnit, this.m_pplr as Player)) {
            this.Build(pmsg.BuildOtherCommand.ut);
          }
          return knHandled;

        case kmidAbortBuildOtherCommand:
          this.AbortBuild(true, pmsg.AbortBuildOtherCommand.ut);
          return knHandled;

        case kmidSelfDestructCommand:
          // Abort WITH refund before the recycle (Deactivate's abort wouldn't refund)
          this.AbortBuild(true);
          this.SelfDestruct();
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstIdle) {
      switch (pmsg.mid) {
        case kmidUpgradeComplete:
          this.m_ff = (this.m_ff & ~kfGobBeingUpgraded) >>> 0;
          this.MarkRedraw();
          return knHandled;

        case kmidBeingUpgraded:
          this.m_ff = (this.m_ff | kfGobBeingUpgraded) >>> 0;
          this.m_unvl.MinSkip();
          return knHandled;

        case kmidReservedUpdate:
          // Keep redrawing while the upgrade effect shows
          if (
            (this.m_pplr as Player).GetUpgrades() &
            (this.m_puntc as MobileUnitBuilderConsts).fUpgradeInProgress
          ) {
            this.MarkRedraw();
            this.m_unvl.MinSkip();
          }
          return super.ProcessStateMachineMessage(st, pmsg);
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstBuildOtherCompleting) {
      switch (pmsg.mid) {
        case kmidReservedEnter: {
          const puntBuild = this.GetBuiltGob();
          if (puntBuild !== null) {
            const wpt = this.FindInitPosition();
            puntBuild.Init(wpt.wx, wpt.wy, this.m_pplr as Player, 0, 0);

            // A Bullpup built by a human goes straight off to mine
            if (
              !(this.m_pplr.GetFlags() & kfPlrComputer) &&
              puntBuild.GetType() === kgtGalaxMiner
            ) {
              const msgT = newMessage(kmidMineCommand, kgidNull, puntBuild.GetId());
              msgT.MoveCommand.wptTarget.wx = kwxInvalid;
              msgT.MoveCommand.wptTarget.wy = 0;
              msgT.MoveCommand.gidTarget = kgidNull;
              msgT.MoveCommand.wptTargetCenter.wx = kwxInvalid;
              msgT.MoveCommand.wptTargetCenter.wy = 0;
              msgT.MoveCommand.tcTargetRadius = 0;
              msgT.MoveCommand.wcMoveDistPerUpdate = 0;
              this.ctx.smm!.SendMsg(msgT);
            }
            // (RALLY_POINTS is compiled out)

            // Notify the BuildMgr that this Unit is complete
            this.ctx.sim!.GetBuildMgr()?.OnBuilt(puntBuild, this);
            // pfrmBuild->OnUnitCompleted(this, ut) — forms UI (host seam)
            this.ctx.host.BuildFormOnUnitCompleted(this, puntBuild.GetUnitType());
            this.ClearBuiltGob();
          }
          this.SetState(kstIdle);
          return knHandled;
        }

        // Eat Update/Exit so they don't route up to BuilderGob's handlers
        case kmidReservedUpdate:
        case kmidReservedExit:
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    return super.ProcessStateMachineMessage(st, pmsg);
  }

  // game/MobileBuilder.cpp:205-213 — give the build form a chance to show
  // progress (host seam) FIRST, then the normal idle processing
  override DefUpdate(): void {
    this.ctx.host.BuildFormDefUpdate(this, this.IsBuildInProgress());
    super.DefUpdate();
  }

  // game/MobileBuilder.cpp:70-77 — EndForm the build form this builder owns
  override Deactivate(): void {
    super.Deactivate();
    this.ctx.host.BuilderDeactivate(this);
  }
}

/** The slice of UnitGroup the BuildMgr drives (UnitGroupMgr.cpp, M3). */
export interface UnitGroupLike {
  GetOwner(): Player;
  OnBuilt(punt: UnitGob): void;
}

interface BuildEntry {
  ut: number;
  pug: UnitGroupLike;
  nArea: number;
  gidBuilder: number;
  pbldeNext: BuildEntry | null;
}

// game/BuildMgr.cpp — the AI build pipeline: UnitGroups request units, idle builders pick
// them up. Linked-list ORDER is deliberate: miners jump the queue, others append.
export class BuildMgr {
  private ctx: SimContext;
  private m_pbldeFirst: BuildEntry | null = null;

  constructor(ctx: SimContext) {
    this.ctx = ctx;
  }

  // game/BuildMgr.cpp:25-59
  BuildUnit(ut: number, pug: UnitGroupLike, nArea: number): boolean {
    const pblde: BuildEntry = { ut, pug, nArea, gidBuilder: kgidNull, pbldeNext: null };

    // Miners jump to the head (the level AI must not run dry)
    if (pblde.ut === kutGalaxMiner) {
      pblde.pbldeNext = this.m_pbldeFirst;
      this.m_pbldeFirst = pblde;
      return true;
    }

    // Append chronologically
    if (this.m_pbldeFirst === null) {
      this.m_pbldeFirst = pblde;
    } else {
      let p = this.m_pbldeFirst;
      while (p.pbldeNext !== null) p = p.pbldeNext;
      p.pbldeNext = pblde;
    }
    return true;
  }

  // game/BuildMgr.cpp:63-93
  OnBuilt(punt: UnitGob, pbldr: BuilderGob): void {
    const utBuilt = punt.GetConsts().ut;
    const gidBuilder = pbldr.GetId();
    const pplrBuilt = punt.GetOwner();

    let prev: BuildEntry | null = null;
    for (let pblde = this.m_pbldeFirst; pblde !== null; prev = pblde, pblde = pblde.pbldeNext) {
      if (
        utBuilt === pblde.ut &&
        gidBuilder === pblde.gidBuilder &&
        pplrBuilt === pblde.pug.GetOwner()
      ) {
        if (prev === null) this.m_pbldeFirst = pblde.pbldeNext;
        else prev.pbldeNext = pblde.pbldeNext;

        pblde.pug.OnBuilt(punt);
        return;
      }
    }
  }

  // snapshot-only (replay-viewer slow-seek fix): the build-entry list is UNSAVABLE in the
  // C++/faithful format — it is re-created on level load by UnitGroup.Activate → BuildUnit — so
  // saveState()/load() omit it. But a mid-game snapshot resume needs it: a unit built after the
  // resume point inherits its entry's UnitGroup aggressiveness via OnBuilt (kfMunt* stance), so a
  // restored sim with an empty list builds units with the DEFAULT stance and diverges within a few
  // hundred blocks — every replay-viewer checkpoint then fails hash validation and re-sims from
  // seed. The UnitGroups themselves round-trip via UnitGroupMgr, so entries are keyed by group
  // INDEX; list ORDER is preserved (miners ride at the head — see BuildUnit).
  private groupList(): UnitGroupLike[] {
    const ugm = this.ctx.sim!.GetLevel()!.GetUnitGroupMgr() as unknown as {
      m_aug: UnitGroupLike[];
    };
    return ugm.m_aug;
  }
  SnapshotBuildEntries(pstm: import('../sm/stream.ts').Stream): void {
    const aug = this.groupList();
    let n = 0;
    for (let p = this.m_pbldeFirst; p !== null; p = p.pbldeNext) n++;
    pstm.WriteWord(n);
    for (let p = this.m_pbldeFirst; p !== null; p = p.pbldeNext) {
      pstm.WriteWord(p.ut & 0xffff);
      pstm.WriteWord(aug.indexOf(p.pug) & 0xffff); // 0xffff if not found (defensive; never expected)
      pstm.WriteDword(p.nArea | 0);
      pstm.WriteWord(p.gidBuilder & 0xffff);
    }
  }
  RestoreBuildEntries(pstm: import('../sm/stream.ts').Stream): void {
    const aug = this.groupList();
    const n = pstm.ReadWord();
    let head: BuildEntry | null = null;
    let tail: BuildEntry | null = null;
    for (let i = 0; i < n; i++) {
      const ut = pstm.ReadWord();
      const gi = pstm.ReadWord();
      const nArea = pstm.ReadDword() | 0;
      const gidBuilder = pstm.ReadWord();
      const pug = aug[gi];
      if (pug === undefined) continue; // group index out of range → drop the entry (defensive)
      const e: BuildEntry = { ut, pug, nArea, gidBuilder, pbldeNext: null };
      if (head === null) head = e;
      else tail!.pbldeNext = e;
      tail = e;
    }
    this.m_pbldeFirst = head;
  }

  // game/BuildMgr.cpp:97-202
  Update(): void {
    if (this.m_pbldeFirst === null) return;

    for (let pblde = this.m_pbldeFirst; pblde !== null; pblde = pblde.pbldeNext) {
      // Clear stale builders (gone or changed hands)
      const pbldr = this.ctx.gobm.GetGob(pblde.gidBuilder) as BuilderGob | null;
      if (pbldr === null || pbldr.GetOwner() !== pblde.pug.GetOwner()) {
        pblde.gidBuilder = kgidNull;
      }
    }

    // Scan the gob list for idle builders of the right type/owner
    for (
      let pgob = this.ctx.gobm.GetFirstGob();
      pgob !== null;
      pgob = this.ctx.gobm.GetNextGob(pgob)
    ) {
      if (!(pgob.GetFlags() & kfGobStructure)) continue;

      const pstru = pgob as StructGob;
      const pstruc = pstru.GetConsts();

      if (!((pstruc.um & kumBuilder) >>> 0)) continue;

      const pbldr = pstru as BuilderGob;
      if (pbldr.IsBuildInProgress()) continue;

      const pplrBuilderOwner = pbldr.GetOwner() as Player;
      const pbldrc = pbldr.GetConsts() as BuilderConsts;

      for (let pblde = this.m_pbldeFirst; pblde !== null; pblde = pblde.pbldeNext) {
        if (pblde.gidBuilder !== kgidNull) continue;
        if (pblde.pug.GetOwner() !== pplrBuilderOwner) continue;

        const um = (1 << pblde.ut) >>> 0;
        if ((pbldrc.umCanBuild & um) >>> 0) {
          if (um & kumStructures) {
            if (!this.ctx.gobm.IsBelowLimit(knLimitStruct, pplrBuilderOwner)) continue;

            const trc = this.ctx.gobm.GetAreaRect(pblde.nArea);

            // Is the structure's reserve space free?
            const pstrucBuild = this.ctx.unitConsts[pblde.ut] as StructConsts;
            if (
              this.ctx.trmap!.IsOccupied(
                trc.left,
                trc.top,
                pstrucBuild.ctxReserve,
                pstrucBuild.ctyReserve,
                kbfStructure | kbfMobileUnit,
              )
            ) {
              continue;
            }

            const msg = newMessage(kmidBuildOtherCommand, ksmidNull, pbldr.GetId());
            msg.BuildOtherCommand.ut = pblde.ut;
            msg.BuildOtherCommand.wpt.wx = WcFromTc(trc.left);
            msg.BuildOtherCommand.wpt.wy = WcFromTc(trc.top);
            this.ctx.smm!.SendMsg(msg);
          } else {
            if (!this.ctx.gobm.IsBelowLimit(knLimitMobileUnit, pplrBuilderOwner)) continue;

            pbldr.Build(pblde.ut);
          }
          pblde.gidBuilder = pbldr.GetId();
          break; // next builder for any remaining entries
        }
      }
    }
  }
}
